import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from 'config';

interface ContactFormData {
  name: string;
  phone: string;
  email: string;
  company: string;
  comment?: string;
  agreement: boolean;
}

const ContactsPage = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    phone: '',
    email: '',
    company: '',
    comment: '',
    agreement: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await axios.post(`${API_URL}/api/callback`, formData);
      setSubmitSuccess(true);
      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        company: '',
        comment: '',
        agreement: false
      });
    } catch (error) {
      setSubmitError('Произошла ошибка при отправке формы. Пожалуйста, попробуйте позже.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  useEffect(() => {
    // Initialize Yandex Map
    const init = () => {
      // @ts-ignore
      const myMap = new window.ymaps.Map('map', {
        center: [55.479965, 37.570528], // Coordinates for Podolsk, ul. 8 Marta, 2
        zoom: 15,
        controls: ['zoomControl'] // Only show zoom controls
      });

      // Add marker
      // @ts-ignore
      const myPlacemark = new window.ymaps.Placemark([55.479965, 37.570528], {
        balloonContent: 'г. Подольск, ул. 8 Марта, дом 2'
      }, {
        preset: 'islands#redDotIcon' // Use a red dot icon for better visibility
      });

      myMap.geoObjects.add(myPlacemark);

      // Disable scroll zoom
      myMap.behaviors.disable('scrollZoom');
    };

    // @ts-ignore
    window.ymaps.ready(init);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Контакты</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        <div>
          <h2 className="text-2xl font-semibold mb-6">Наши контакты</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Москва</h3>
              <div className="space-y-2">
                <p><strong>Офис и склад:</strong></p>
                <p>г. Подольск, ул. 8 Марта, дом 2</p>
                <p>Тел: <a href="tel:88002505710" className="text-primary hover:underline">8 800 250 57 10</a></p>
                <p>Тел: <a href="tel:79266929899" className="text-primary hover:underline">+7 926 692 98 99</a></p>
                <p>Тел: <a href="tel:79653957895" className="text-primary hover:underline">+7 965 395 78 95</a></p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Санкт-Петербург</h3>
              <div className="space-y-2">
                <p><strong>Офис:</strong></p>
                <p>ул. Кирочная, д. 33</p>
                <p>Тел: <a href="tel:+79219669444" className="text-primary hover:underline">+8 953 368 75 36</a></p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Оренбург</h3>
              <div className="space-y-2">
                <p><strong>Офис:</strong></p>
                <p>ул. Липовая, д. 21, оф. 60</p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Email</h3>
              <p><a href="mailto:info@teplogrand.pro" className="text-primary hover:underline">info@teplogrand.pro</a></p>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-6">Заказать звонок</h2>
          
          {submitSuccess ? (
            <div className="text-center py-8">
              <div className="text-green-600 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold mb-2">Заявка успешно отправлена</h2>
              <p className="text-gray-600">Мы свяжемся с вами в ближайшее время.</p>
              <button
                onClick={() => setSubmitSuccess(false)}
                className="mt-6 bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition-colors"
              >
                Отправить еще одну заявку
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {submitError && (
                <div className="bg-red-50 text-red-600 p-4 rounded-md">
                  {submitError}
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Ваше имя *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Телефон *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Почта для обратной связи *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                  Компания *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1">
                  Комментарий
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  rows={4}
                  value={formData.comment}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="agreement"
                  name="agreement"
                  required
                  checked={formData.agreement}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label htmlFor="agreement" className="ml-2 text-sm text-gray-600">
                  Я согласен на обработку персональных данных *
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary text-white px-6 py-3 rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
              </button>

              <div className="text-sm text-gray-500">
                <p>* — обязательные поля</p>
                <p className="mt-2">
                  Мы перезвоним вам в течение рабочего дня (пн-пт с 9:00 до 18:00)
                </p>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6">Как нас найти</h2>
        <div className="h-[400px] bg-gray-100 rounded-lg overflow-hidden">
          <div id="map" className="w-full h-full"></div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-semibold mb-6">Региональные представительства</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-2">Москва</h3>
            <p>г. Подольск, ул. 8 Марта, дом 2</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-2">Санкт-Петербург</h3>
            <p>ул. Кирочная, д. 33</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-2">Оренбург</h3>
            <p>ул. Липовая, д. 21, оф. 60</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsPage; 